import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAdminAuth } from "./AdminAuth";
import { listProjects, createUser, createProject, formatApiErrorDetail } from "./adminApi";

function Field({ label, ...props }) {
  return (
    <div>
      <label className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">{label}</label>
      <input
        {...props}
        className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white placeholder:text-white/25 transition-colors"
      />
    </div>
  );
}

export default function AdminProjects() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const isSuper = user?.platform_role === "superadmin";

  // create dialogs state
  const [nu, setNu] = useState({ email: "", name: "", password: "" });
  const [np, setNp] = useState({ slug: "", name: "", artist: "", owner_email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setProjects(await listProjects());
    } catch (e) {
      toast.error("Could not load projects");
    }
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const submitUser = async () => {
    setBusy(true);
    try {
      await createUser({ email: nu.email.toLowerCase(), name: nu.name, password: nu.password, platform_role: null });
      toast.success("Artist account created");
      setNu({ email: "", name: "", password: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitProject = async () => {
    setBusy(true);
    try {
      await createProject({ ...np, owner_email: np.owner_email.toLowerCase(), tagline: "A FANLY Exclusive" });
      toast.success("Project created");
      setNp({ slug: "", name: "", artist: "", owner_email: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#050505]" data-testid="admin-projects">
      <header className="border-b border-white/10 px-6 sm:px-10 py-5 flex items-center justify-between">
        <div>
          <p className="font-mono-x text-[10px] tracking-[0.4em] text-[#737373] uppercase">FANLY · Studio</p>
          <h1 className="font-display text-2xl font-extrabold tracking-tighter">Projects</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:flex items-center gap-2 font-mono-x text-[11px] text-[#8a8a8a]">
            {isSuper && <ShieldCheck className="w-3.5 h-3.5 text-[#FF2A2A]" />}
            {user?.email}
          </span>
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="flex items-center gap-2 font-mono-x text-[11px] tracking-widest uppercase text-[#737373] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="px-6 sm:px-10 py-10 max-w-4xl mx-auto">
        {isSuper && (
          <div className="flex flex-wrap gap-3 mb-10">
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="new-artist-btn" className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
                  <Plus className="w-4 h-4" /> New artist
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0e0e0e] border-white/10 text-white">
                <DialogHeader><DialogTitle className="font-display">New artist account</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <Field label="Email" data-testid="nu-email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="artist@email.com" />
                  <Field label="Name" data-testid="nu-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Artist name" />
                  <Field label="Temp password" data-testid="nu-password" type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} placeholder="Set a password" />
                </div>
                <DialogFooter>
                  <button data-testid="nu-submit" onClick={submitUser} disabled={busy} className="bg-white text-black rounded-full px-5 py-2 font-semibold disabled:opacity-60">
                    {busy ? "…" : "Create artist"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="new-project-btn" className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
                  <Plus className="w-4 h-4" /> New project
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0e0e0e] border-white/10 text-white">
                <DialogHeader><DialogTitle className="font-display">New release / project</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <Field label="Slug (URL)" data-testid="np-slug" value={np.slug} onChange={(e) => setNp({ ...np, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="e.g. midnight" />
                  <Field label="Release name" data-testid="np-name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="MIDNIGHT" />
                  <Field label="Artist" data-testid="np-artist" value={np.artist} onChange={(e) => setNp({ ...np, artist: e.target.value })} placeholder="Artist name" />
                  <Field label="Owner artist email" data-testid="np-owner" value={np.owner_email} onChange={(e) => setNp({ ...np, owner_email: e.target.value })} placeholder="artist@email.com (must exist)" />
                </div>
                <DialogFooter>
                  <button data-testid="np-submit" onClick={submitProject} disabled={busy} className="bg-white text-black rounded-full px-5 py-2 font-semibold disabled:opacity-60">
                    {busy ? "…" : "Create project"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {projects === null ? (
          <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : projects.length === 0 ? (
          <p className="text-[#737373]">No projects yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {projects.map((p) => (
              <div key={p.slug} data-testid={`project-card-${p.slug}`} className="group border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF2A2A]/50 transition-colors">
                <div className="h-48 bg-[#111] relative">
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
                <div className="p-5">
                  <p className="font-mono-x text-[10px] tracking-[0.3em] text-[#737373] uppercase">{p.artist}</p>
                  <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{p.name}</h3>
                  <div className="flex items-center gap-4">
                    <button data-testid={`edit-${p.slug}`} onClick={() => navigate(`/admin/projects/${p.slug}`)} className="font-mono-x text-[11px] tracking-widest uppercase text-[#FF2A2A] hover:text-white transition-colors">
                      Manage
                    </button>
                    <a href={p.public_path} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono-x text-[11px] tracking-widest uppercase text-[#737373] hover:text-white transition-colors">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
